import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAdminEntity from '../../../hooks/useAdminEntity';
import { getInvestorProjectDetailEndpoint } from '../services/investorService';
import { getProjectNearByPlaceMetricsEndpoint } from '../services/projectService';
import { InvestorProjectDetail } from '../models/investors';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';

export interface NearByPlaceRow {
  near_by_place_id: string;
  name: string;
  distance_mt: number | null;
  time_seconds: number | null;
}

export const useNearByPlaceMetrics = () => {
  const { id: projectId = '' } = useParams();

  const { isLoading: isApiLoading, callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const [errorAlert, setErrorAlert] = useState<any>(null);
  const [sweetAlert, setSweetAlert] = useState(false);
  const [alertProps, setAlertProps] = useState<any>({});

  const [nearByPlaces, setNearByPlaces] = useState<NearByPlaceRow[]>([]);
  const [loadingTable, setLoadingTable] = useState(true);
  const [calculating, setCalculating] = useState(false);

  const globalAlertOptions = {
    success: false,
    warning: true,
    onConfirm: () => setErrorAlert(null),
    title: 'Error',
    text: errorAlert || '',
  };

  const getInvestorProject = useCallback(
    () => getInvestorProjectDetailEndpoint(projectId, token),
    [projectId, token],
  );
  const [investorProject] = useAdminEntity<InvestorProjectDetail>(
    getInvestorProject,
    setErrorAlert,
  );

  useEffect(() => {
    if (investorProject?.near_by_places) {
      const rows: NearByPlaceRow[] = investorProject.near_by_places.map(
        (place) => ({
          near_by_place_id: place.id,
          name: place.name || 'Sin nombre',
          distance_mt: place.distance_mt,
          time_seconds: place.time_seconds,
        }),
      );

      setNearByPlaces(rows);
      setLoadingTable(false);
    }
  }, [investorProject]);

  const handleCalculateMetrics = useCallback(async () => {
    setCalculating(true);
    const { status, data } = await callEndpoint(
      getProjectNearByPlaceMetricsEndpoint(projectId, token),
    );

    if (status === 200) {
      const response = fetchResponseAdapter(data);
      const results: NearByPlaceRow[] = (response.data?.near_by_places || []).map(
        (item: any) => ({
          near_by_place_id: item.near_by_place_id,
          name: item.name || 'Sin nombre',
          distance_mt: item.distance_mt,
          time_seconds: item.time_seconds,
        }),
      );
      setNearByPlaces(results);

      setAlertProps({
        success: true,
        title: 'Cálculo completado',
        text: 'Las distancias y tiempos han sido calculados correctamente.',
        onConfirm: () => setSweetAlert(false),
      });
      setSweetAlert(true);
    } else {
      setAlertProps({
        danger: true,
        title: 'Error',
        text: 'Ocurrió un problema al calcular las métricas. Por favor, intenta de nuevo.',
        onConfirm: () => setSweetAlert(false),
      });
      setSweetAlert(true);
    }

    setCalculating(false);
  }, [projectId, token, callEndpoint]);

  const isLoading = !investorProject;

  return {
    isLoading,
    loadingTable,
    calculating,
    nearByPlaces,
    handleCalculateMetrics,
    sweetAlert,
    alertProps,
    globalAlertOptions,
  };
};
